import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, XCircle, Mail } from 'lucide-react';
import api from '../../services/api';

const VerifyEmail: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [loading, setLoading] = useState(true);
    const [verified, setVerified] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!token) {
            setError('Token de vérification manquant');
            setLoading(false);
            return;
        }

        verifyEmail();
    }, [token]);

    const verifyEmail = async () => {
        try {
            setLoading(true);
            const response = await api.post('/auth/verify-email', { token });

            if (response.data) {
                setVerified(true);
                // Redirect to password creation page after 2 seconds
                setTimeout(() => {
                    navigate(`/set-password?token=${token}`);
                }, 2000);
            }
        } catch (err: any) {
            console.error('Email verification error:', err);
            setError(
                err.response?.data?.detail ||
                'Une erreur est survenue lors de la vérification de votre email'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleResendEmail = async () => {
        navigate('/resend-verification');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardContent className="flex flex-col items-center space-y-4 p-6">
                        <Loader2 className="h-12 w-12 animate-spin text-emerald-600" />
                        <p className="text-center text-gray-600">
                            Vérification de votre email en cours...
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (verified) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardContent className="flex flex-col items-center space-y-4 p-6">
                        <CheckCircle className="h-12 w-12 text-green-600" />
                        <div className="text-center space-y-2">
                            <h2 className="text-xl font-semibold text-gray-900">
                                Email vérifié avec succès!
                            </h2>
                            <p className="text-gray-600">
                                Vous allez être redirigé vers la page de création de mot de passe...
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <div className="mx-auto mb-4">
                        <Mail className="h-12 w-12 text-emerald-600" />
                    </div>
                    <CardTitle className="text-2xl">Vérification de l'email</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {error && (
                        <Alert variant="destructive">
                            <XCircle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    <div className="text-center space-y-4">
                        <p className="text-gray-600">
                            {error
                                ? 'La vérification a échoué. Veuillez vérifier que vous avez bien cliqué sur le lien dans votre email ou demandez un nouvel email.'
                                : 'Une erreur est survenue lors de la vérification.'
                            }
                        </p>

                        <div className="space-y-2">
                            <Button
                                onClick={() => window.location.reload()}
                                className="w-full"
                                variant="outline"
                            >
                                Réessayer
                            </Button>

                            <Button
                                onClick={handleResendEmail}
                                className="w-full"
                                variant="ghost"
                            >
                                Renvoyer l'email de vérification
                            </Button>
                        </div>

                        <div className="pt-4 border-t">
                            <p className="text-sm text-gray-500">
                                Retour à la{' '}
                                <button
                                    onClick={() => navigate('/login')}
                                    className="text-emerald-600 hover:underline font-medium"
                                >
                                    page de connexion
                                </button>
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default VerifyEmail;
